'use client';

import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import { useCallback, useEffect, Dispatch, SetStateAction, useLayoutEffect } from 'react';
import { useLocalStorage, useSessionStorage } from 'usehooks-ts';
import { create } from 'zustand';

export type StorageType = 'search-params' | 'local' | 'session' | 'memory';

type MemoryStoreState = {
    values: Record<string, unknown>;
    setValue: <T>(key: string, value: T | null) => void;
    _isHydrated: boolean;
};

// Single zustand store for all memory storage keys (created outside hook)
const useMemoryStore = create<MemoryStoreState>((set) => ({
    values: {},
    setValue: <T>(key: string, value: T | null) =>
        set((state) => ({
            values: { ...state.values, [key]: value },
        })),
    _isHydrated: false,
}));

export const useStorage = <T = string>(
    type: StorageType,
    name: string,
    defaultValue: T | null = null,
): [T | null, Dispatch<SetStateAction<T | null>>] => {
    const isHydrated = useMemoryStore((s) => s._isHydrated);
    // Always call all hooks to satisfy React hooks rules
    const [localValue, setLocalValue] = useLocalStorage<T | null>(name, defaultValue);
    const [sessionValue, setSessionValue] = useSessionStorage<T | null>(name, defaultValue);
    const searchParams = useSearchParams();
    const router = useRouter();
    const pathname = usePathname();

    // Memory Storage (Zustand) - use single store with key-based values
    const memoryValue = useMemoryStore((state) => (state.values[name] as T | null) ?? defaultValue);

    // Define all callbacks upfront to satisfy React hooks rules
    const setSearchParamsValue = useCallback<Dispatch<SetStateAction<T | null>>>(
        (newValueOrUpdater) => {
            // Get current value for callback support
            const currentStringValue = searchParams.get(name);
            let currentValue: T | null = defaultValue;
            if (currentStringValue !== null) {
                try {
                    currentValue = JSON.parse(currentStringValue) as T;
                } catch {
                    currentValue = currentStringValue as T;
                }
            }

            // Apply updater function if provided, otherwise use value directly
            const newValue =
                typeof newValueOrUpdater === 'function'
                    ? (newValueOrUpdater as (prevValue: T | null) => T | null)(currentValue)
                    : newValueOrUpdater;

            const params = new URLSearchParams(searchParams.toString());

            if (newValue === null || newValue === '') {
                params.delete(name);
            } else {
                // For search-params, convert value to string
                const stringValue = typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
                params.set(name, stringValue);
            }

            const newSearchParams = params.toString();
            const newUrl = newSearchParams ? `${pathname}?${newSearchParams}` : pathname;
            router.push(newUrl, { scroll: false });
        },
        [name, pathname, router, searchParams, defaultValue],
    );

    const setMemoryValue = useCallback<Dispatch<SetStateAction<T | null>>>(
        (newValueOrUpdater) => {
            // Get current value for callback support
            const currentValue = (useMemoryStore.getState().values[name] as T | null) ?? defaultValue;

            // Apply updater function if provided, otherwise use value directly
            const newValue =
                typeof newValueOrUpdater === 'function'
                    ? (newValueOrUpdater as (prevValue: T | null) => T | null)(currentValue)
                    : newValueOrUpdater;

            useMemoryStore.getState().setValue(name, newValue);
        },
        [name, defaultValue],
    );

    // Initialize memory store with defaultValue if not already set
    useEffect(() => {
        const currentValue = useMemoryStore.getState().values[name];
        if (currentValue === undefined) {
            useMemoryStore.getState().setValue(name, defaultValue);
        }
    }, [name, defaultValue]);

    useLayoutEffect(() => {
        useMemoryStore.setState({ _isHydrated: true });
    }, []);

    // Return defaultValue during SSR or before hydration
    if (!isHydrated) {
        return [defaultValue, () => {}];
    }

    if (!type || !name) {
        return [defaultValue, () => {}];
    }

    // Return appropriate values based on type
    if (type === 'local') {
        return [localValue, setLocalValue];
    }

    if (type === 'session') {
        return [sessionValue, setSessionValue];
    }

    if (type === 'search-params') {
        const stringValue = searchParams.get(name);
        // Try to parse as JSON, fallback to string
        let value: T | null = defaultValue;
        if (stringValue !== null) {
            try {
                value = JSON.parse(stringValue) as T;
            } catch {
                // If parsing fails, treat as string
                value = stringValue as T;
            }
        }
        return [value, setSearchParamsValue];
    }

    if (type === 'memory') {
        return [memoryValue, setMemoryValue];
    }

    // Fallback (should never reach here due to TypeScript)
    return [null, () => {}];
};

export const useStorageValue = (
    value?: string | null,
): [string | null | undefined, Dispatch<SetStateAction<string | null>>] => {
    let type: StorageType = 'memory';
    let name: string = 'temp';
    let isMemoryValue = false;
    const valueParts = (value ?? '').split(':');
    if (
        ['search-params', 'local', 'session', 'memory'].includes(valueParts[0]) &&
        (valueParts?.[1]?.length ?? 0) > 0
    ) {
        type = valueParts[0] as StorageType;
        const valueName = [...valueParts];
        valueName.shift();
        name = valueName.join();
        isMemoryValue = true;
    }
    const storageValue = useStorage(type, name);

    return isMemoryValue ? storageValue : [value, () => {}];
};
